/**
 * UI Language Manager
 * Manages user interface localization independently from document language processing
 */

// English is the fallback for every missing key, so it is always bundled.
import enTranslations from './locales/en.json';
import type {
  LanguageCode,
  UILanguages,
  UILanguageConfig,
} from '../core/language-constants';
import { getUILanguages, DEFAULT_LANGUAGE } from '../core/language-constants';
import { STORAGE_KEYS } from '../core/app-constants';
import type {
  IUIManager,
  TranslationParams,
  LanguageChangeCallback,
} from './ui-manager.types';
import { logger } from '../core/logger';

// Type definitions
export type TranslationData = Record<string, any>;

// The other languages load on demand (#133): a user needs one of them, and
// each is ~6 kB gzip. Explicit imports so Vite gives each its own chunk.
const LOCALE_LOADERS: Partial<
  Record<LanguageCode, () => Promise<{ default: TranslationData }>>
> = {
  de: () => import('./locales/de.json'),
  es: () => import('./locales/es.json'),
  pt: () => import('./locales/pt.json'),
  cs: () => import('./locales/cs.json'),
};
export type {
  TranslationParams,
  LanguageChangeCallback,
} from './ui-manager.types';

class UIManager implements IUIManager {
  private fallbackLanguage: LanguageCode;
  private currentLanguage: LanguageCode;
  private translations: Record<string, TranslationData>;
  private supportedUILanguages: UILanguages;
  private storageKey: string;
  private changeCallbacks: LanguageChangeCallback[];

  constructor() {
    this.fallbackLanguage = DEFAULT_LANGUAGE;
    this.currentLanguage = this.fallbackLanguage;
    this.translations = { en: enTranslations };
    this.supportedUILanguages = getUILanguages();
    this.storageKey = STORAGE_KEYS.UI_LANGUAGE;
    this.changeCallbacks = [];
  }

  /**
   * Initialize the UI manager
   */
  async initialize(): Promise<void> {
    try {
      // Detect and set initial language
      const preferredLanguage = this.detectPreferredLanguage();
      await this.setLanguage(preferredLanguage);

      logger.debug(
        `UI Manager initialized with language: ${this.currentLanguage}`
      );
    } catch (error) {
      logger.warn('Failed to initialize UI Manager:', error);
      await this.setLanguage(this.fallbackLanguage);
    }
  }

  /**
   * Detect user's preferred UI language
   */
  detectPreferredLanguage(): LanguageCode {
    // 1. Check stored preference
    const storedLanguage = this.getStoredLanguage();
    if (storedLanguage && this.isUILanguageSupported(storedLanguage)) {
      return storedLanguage as LanguageCode;
    }

    // 2. Check browser language
    const browserLanguages = this.getBrowserLanguages();
    for (const lang of browserLanguages) {
      const langCode = lang.split('-')[0] ?? lang; // Extract base language (e.g., 'en' from 'en-US')
      if (this.isUILanguageSupported(langCode)) {
        return langCode as LanguageCode;
      }
    }

    // 3. Fallback to default
    return this.fallbackLanguage;
  }

  /**
   * Get browser languages in order of preference
   */
  getBrowserLanguages(): readonly string[] {
    const languages: string[] = [];

    if (navigator.languages) {
      languages.push(...navigator.languages);
    } else if (navigator.language) {
      languages.push(navigator.language);
    }

    return languages;
  }

  /**
   * Get stored language preference
   */
  getStoredLanguage(): string | null {
    try {
      return localStorage.getItem(this.storageKey);
    } catch (error) {
      logger.warn('Unable to access localStorage:', error);
      return null;
    }
  }

  /**
   * Store language preference
   */
  storeLanguage(languageCode: LanguageCode): void {
    try {
      localStorage.setItem(this.storageKey, languageCode);
    } catch (error) {
      logger.warn('Unable to store language preference:', error);
    }
  }

  /**
   * Check if a UI language is supported
   */
  isUILanguageSupported(languageCode: string): boolean {
    return languageCode in this.supportedUILanguages;
  }

  /**
   * Load translations for a specific language
   */
  async loadTranslations(languageCode: LanguageCode): Promise<TranslationData> {
    if (!this.isUILanguageSupported(languageCode)) {
      throw new Error(`Unsupported UI language: ${languageCode}`);
    }

    try {
      const load = LOCALE_LOADERS[languageCode];
      const translations = load ? (await load()).default : enTranslations;
      this.translations[languageCode] = translations;
      return translations;
    } catch (error) {
      logger.warn(`Failed to load ${languageCode} translations:`, error);

      // Fallback to English if not already loading English
      if (languageCode !== this.fallbackLanguage) {
        return this.loadTranslations(this.fallbackLanguage);
      }

      // Final fallback to English
      this.translations[languageCode] = this.getTranslations('en');
      return this.translations[languageCode];
    }
  }

  /**
   * Get the loaded translations for a language; English until it is loaded
   */
  getTranslations(languageCode: LanguageCode): TranslationData {
    return this.translations[languageCode] ?? enTranslations;
  }

  /**
   * Set current UI language
   */
  async setLanguage(languageCode: LanguageCode): Promise<void> {
    if (!this.isUILanguageSupported(languageCode)) {
      logger.warn(
        `Unsupported UI language: ${languageCode}, falling back to ${this.fallbackLanguage}`
      );
      languageCode = this.fallbackLanguage;
    }

    // Load translations if not already loaded
    if (!this.translations[languageCode]) {
      try {
        await this.loadTranslations(languageCode);
      } catch (_error) {
        logger.warn(
          `Could not load translations for ${languageCode}, using fallback`
        );
        this.translations[languageCode] = this.getTranslations(languageCode);
      }
    }

    const previousLanguage = this.currentLanguage || this.fallbackLanguage;
    this.currentLanguage = languageCode;

    // Store preference
    this.storeLanguage(languageCode);

    // Update UI
    this.updateUI();

    // Notify callbacks
    this.notifyLanguageChange(languageCode, previousLanguage);

    logger.debug(
      `UI language changed from ${previousLanguage} to ${languageCode}`
    );
  }

  /**
   * Get translation for a key
   */
  t(key: string, params: TranslationParams = {}): string {
    let translation = this.lookup(this.currentLanguage, key, params.count);

    // Fallback to English if translation not found
    if (
      translation === undefined &&
      this.currentLanguage !== this.fallbackLanguage
    ) {
      translation = this.lookup(this.fallbackLanguage, key, params.count);
    }

    // Final fallback to key itself
    if (translation === undefined) {
      logger.debug(`Missing translation for key: ${key}`);
      translation = key;
    }

    // Replace parameters
    return this.replaceParams(translation, params);
  }

  /**
   * One language's text for a key. With a numeric count, the plural form
   * of that language comes first (#152): `key_one`, `key_few`, … as
   * Intl.PluralRules names them, then `key_other`, then the plain key.
   */
  private lookup(
    language: string,
    key: string,
    count: string | number | undefined
  ): any {
    const translations = this.translations[language] || {};
    if (typeof count === 'number') {
      const form = new Intl.PluralRules(language).select(count);
      for (const suffix of [form, 'other']) {
        const plural = this.getNestedValue(translations, `${key}_${suffix}`);
        if (plural !== undefined) return plural;
      }
    }
    return this.getNestedValue(translations, key);
  }

  /**
   * Get nested value from object using dot notation
   */
  getNestedValue(obj: TranslationData, key: string): any {
    return key.split('.').reduce((current, k) => current && current[k], obj);
  }

  /**
   * Replace parameters in translation string
   */
  replaceParams(text: string, params: TranslationParams): string {
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return params[key] !== undefined ? String(params[key]) : match;
    });
  }

  /**
   * Update all UI elements with current translations
   */
  updateUI(): void {
    // Update elements with data-i18n attribute
    document.querySelectorAll('[data-i18n]').forEach((element) => {
      const key = element.getAttribute('data-i18n');
      if (!key) return;

      const translation = this.t(key);

      if (element instanceof HTMLInputElement) {
        if (element.type === 'button' || element.type === 'submit') {
          element.value = translation;
        } else if (element.placeholder !== undefined) {
          element.placeholder = translation;
        }
      } else {
        element.textContent = translation;
      }
    });

    // Update elements with data-i18n-title attribute
    document.querySelectorAll('[data-i18n-title]').forEach((element) => {
      const key = element.getAttribute('data-i18n-title');
      if (!key) return;

      if (element instanceof HTMLElement) {
        element.title = this.t(key);
      }
    });

    // Update HTML lang attribute
    document.documentElement.lang = this.currentLanguage;
  }

  /**
   * Register callback for language changes
   */
  onLanguageChange(callback: LanguageChangeCallback): void {
    this.changeCallbacks.push(callback);
  }

  /**
   * Notify all callbacks of language change
   */
  notifyLanguageChange(
    newLanguage: LanguageCode,
    previousLanguage: LanguageCode
  ): void {
    this.changeCallbacks.forEach((callback) => {
      try {
        callback(newLanguage, previousLanguage);
      } catch (error) {
        logger.error('Error in language change callback:', error);
      }
    });
  }

  /**
   * Get current UI language
   */
  getCurrentLanguage(): LanguageCode {
    return this.currentLanguage;
  }

  /**
   * Get all supported UI languages
   */
  getSupportedLanguages(): UILanguages {
    return this.supportedUILanguages;
  }

  /**
   * Get all supported UI languages (alias for backward compatibility)
   */
  getSupportedUILanguages(): UILanguages {
    return this.supportedUILanguages;
  }

  /**
   * Get language options for dropdown
   */
  getLanguageOptions(): UILanguageConfig[] {
    return Object.values(this.supportedUILanguages);
  }

  /**
   * Get display name for language
   */
  getLanguageDisplayName(languageCode: LanguageCode, native = false): string {
    const lang = this.supportedUILanguages[languageCode];
    if (!lang) return 'Unknown';
    return native ? lang.nativeName : lang.name;
  }

  /**
   * Format a translation with parameters
   */
  format(key: string, params: TranslationParams = {}): string {
    return this.t(key, params);
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.UIManager = UIManager;
}

export default UIManager;
